import { useFormik } from "formik";
import { useFormSubmission } from "AttendanceForm/Utils/Forms/useFormSubmission";
import { RowModel } from "AttendanceForm/Types/types";
import { ADD_ATTENDANCE_RECORDS } from "AttendanceForm/Utils/Queries/LearnerDetails";
import {
  AttendanceCreationMethod,
  AttendanceStatus,
  Status,
} from "AttendanceForm/Pages/LearnerDetails/utils";
import { SessionDetails } from "AttendanceForm/Utils/GraphQl/AttendanceFormData";

interface UpdateAttendanceAndNotesByLearnerId {
  learnerId: string;
  attendanceStatus: AttendanceStatus;
  notes: string;
}

export interface AddLearnerDetailsFormValues {
  newAdhocAttendees: RowModel[];
  deletedLearnerIds?: string[];
  updateAttendanceAndNotesByLearnerId?: UpdateAttendanceAndNotesByLearnerId[];
  attendanceCreationMethod?: AttendanceCreationMethod.SUPPLIER;
  sessionDetails?: SessionDetails;
  allAttendees: RowModel[];
}

const defaultValues: AddLearnerDetailsFormValues = {
  newAdhocAttendees: null,
  deletedLearnerIds: [],
  updateAttendanceAndNotesByLearnerId: [],
  allAttendees: null,
  attendanceCreationMethod: AttendanceCreationMethod.SUPPLIER,
};

interface UseAddLearnerDetailsFormProps {
  initialValues?: Partial<AddLearnerDetailsFormValues>;
  sessionDetails?: SessionDetails;
  trainerEmail?: string;
  eventHash?: string;
}

export const useAddLearnerDetailsForm = ({
  initialValues,
  sessionDetails,
  trainerEmail,
  eventHash,
}: UseAddLearnerDetailsFormProps = {}) => {
  const form = useFormik<AddLearnerDetailsFormValues>({
    initialValues: {
      ...defaultValues,
      ...initialValues,
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => {},
  });

  const mapToEnum = (attendanceStatus: AttendanceStatus) => {
    let value;
    switch (attendanceStatus) {
      case AttendanceStatus.Attended:
        value = Status.ATTENDED;
        break;
      case AttendanceStatus.NotAttended:
        value = Status.DID_NOT_ATTEND;
        break;
      default:
        value = Status.NONE;
    }
    return value;
  };

  const { loading, onSubmit } = useFormSubmission({
    form: form,
    map: (values: AddLearnerDetailsFormValues) => ({
      attendanceRecords: {
        newAdhocAttendees: values.allAttendees
          .filter((attendee) => !attendee.isExistingAttendanceRecord)
          .map((attendee) => {
            return {
              attendanceStatus: mapToEnum(attendee.attended),
              email: attendee.email,
              notes: attendee.notes,
              firstName: attendee.firstname,
              lastName: attendee.lastname,
            };
          }),
        sessionDetails: sessionDetails,
        deletedLearnerIds: values.deletedLearnerIds,
        updateAttendanceAndNotesByLearnerId: values.allAttendees
          .filter((attendee) => attendee.isExistingAttendanceRecord)
          .map((attendee) => {
            return {
              learnerId: attendee.id,
              attendanceStatus: mapToEnum(attendee.attended),
              notes: attendee.notes,
            };
          }),
        trainerEmail: trainerEmail,
        attendanceCreationMethod: AttendanceCreationMethod.SUPPLIER,
        eventHash: eventHash,
      },
    }),
    query: ADD_ATTENDANCE_RECORDS,
  });

  return {
    form,
    onSubmit,
    loading,
  };
};
